import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

/* ═══════════════════════════════════════════════════════════════════
   ZertivoLoader — cinematic full-page loading state.

   Why so much choreography?
   - Loading screens are a brand moment. Most SaaS ship a generic spinner;
     ours is a 3-second trailer for what Zertivo IS: trust being verified
     in real time. Concentric pulses (audit), italic-green wordmark
     (brand DNA), cycling status copy (transparency).
   - All CSS / Framer Motion — no extra deps, no Lottie, no SVG export.
   - Works on warm-cream AND dark themes via theme-aware colors.

   Props
     fullScreen  boolean  — center fixed in viewport (default true)
     status      string   — override the cycling status text (optional)
     compact     boolean  — short layout for inline / modal use
   ═══════════════════════════════════════════════════════════════════ */
const ease = [0.22, 1, 0.36, 1];

const STATUS_PHRASES = [
  'Vertrauen wird verifiziert',
  'Audit-Daten werden geladen',
  'Signaturen werden geprüft',
  'Trust-Score wird berechnet',
];

export default function ZertivoLoader({
  fullScreen = true,
  status,
  compact = false,
}) {
  const [phraseIndex, setPhraseIndex] = useState(0);

  useEffect(() => {
    if (status) return undefined;
    const id = setInterval(() => {
      setPhraseIndex((i) => (i + 1) % STATUS_PHRASES.length);
    }, 1800);
    return () => clearInterval(id);
  }, [status]);

  const text = status || STATUS_PHRASES[phraseIndex];

  const wrapper = fullScreen
    ? 'fixed inset-0 z-[1000] flex items-center justify-center bg-[#FAFAFA] dark:bg-[#060807]'
    : 'flex items-center justify-center w-full';

  return (
    <div className={wrapper} data-testid="zertivo-loader">
      <div className={`flex flex-col items-center ${compact ? 'gap-4' : 'gap-8'}`}>
        {/* Animated trust mark — concentric pulses + central glyph */}
        <div
          className={`relative ${compact ? 'w-20 h-20' : 'w-32 h-32 sm:w-40 sm:h-40'}`}
          aria-hidden
        >
          {/* Three pulses staggered to feel like an audit beacon */}
          {[0, 0.6, 1.2].map((delay, i) => (
            <motion.span
              key={i}
              className="absolute inset-0 rounded-full border border-[#00D387]/35"
              initial={{ scale: 0.45, opacity: 0 }}
              animate={{ scale: [0.45, 1.6], opacity: [0.7, 0] }}
              transition={{
                duration: 2.4,
                repeat: Infinity,
                ease: 'easeOut',
                delay,
              }}
            />
          ))}

          {/* Slow rotating gradient ring — quiet luxury */}
          <motion.span
            className="absolute inset-2 rounded-full"
            style={{
              background:
                'conic-gradient(from 0deg, rgba(0,211,135,0.08) 0deg, rgba(0,211,135,0.55) 80deg, rgba(0,211,135,0.08) 200deg, rgba(0,211,135,0) 360deg)',
              padding: '2px',
              maskImage:
                'radial-gradient(circle, transparent 60%, black 62%, black 100%)',
              WebkitMaskImage:
                'radial-gradient(circle, transparent 60%, black 62%, black 100%)',
            }}
            animate={{ rotate: 360 }}
            transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
          />

          {/* Static halo behind the disc */}
          <span
            className="absolute inset-6 rounded-full"
            style={{
              background:
                'radial-gradient(circle, rgba(0,211,135,0.18), rgba(0,211,135,0) 70%)',
              filter: 'blur(2px)',
            }}
          />

          {/* Central disc */}
          <motion.div
            className="absolute inset-7 rounded-full bg-[#0F8A5F] shadow-[0_8px_28px_rgba(15,138,95,0.32)] flex items-center justify-center"
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.9, ease, delay: 0.1 }}
          >
            {/* Custom shield + check glyph — same DNA as the navbar logo */}
            <motion.svg
              viewBox="0 0 24 24"
              className={`text-white ${compact ? 'w-6 h-6' : 'w-9 h-9 sm:w-11 sm:h-11'}`}
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 1 }}
              transition={{ duration: 1.2, ease, delay: 0.4 }}
            >
              <motion.path
                d="M12 2.5L20 5.5v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10v-6L12 2.5z"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1.0, ease, delay: 0.4 }}
              />
              <motion.path
                d="M8.5 12l2.5 2.5L15.5 10"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.7, ease, delay: 1.05 }}
              />
            </motion.svg>
          </motion.div>
        </div>

        {/* Brand wordmark + italic accent (italic green = brand DNA) */}
        {!compact && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease, delay: 0.5 }}
            className="text-center"
          >
            <h1
              className="font-manrope font-extralight text-[28px] sm:text-[34px] tracking-tight text-[#09090B] dark:text-[#FAFAFA] leading-none"
              data-testid="zertivo-loader-wordmark"
            >
              Zertivo
              <em className="not-italic font-light text-[#0F8A5F] italic">.</em>
            </h1>
            <p className="mt-1 text-[10.5px] sm:text-[11px] font-bold tracking-[0.22em] uppercase text-[#71717A]">
              Digital Trust Platform
            </p>
          </motion.div>
        )}

        {/* Cycling status text — adds calm motion + signals progress */}
        <div
          className={`overflow-hidden ${compact ? 'h-4' : 'h-5'} flex items-center`}
          aria-live="polite"
          data-testid="zertivo-loader-status"
        >
          <motion.span
            key={text}
            initial={{ y: 18, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -18, opacity: 0 }}
            transition={{ duration: 0.5, ease }}
            className={`font-light tracking-tight text-[#52525B] dark:text-[#A1A1AA] ${compact ? 'text-[12px]' : 'text-[13.5px] sm:text-[14px]'}`}
          >
            {text}
            <em className="not-italic text-[#0F8A5F] italic">…</em>
          </motion.span>
        </div>

        {/* Animated bottom accent — pulses with the trust mark */}
        {!compact && (
          <div className="relative h-[2px] w-32 overflow-hidden rounded-full bg-[#E4E4E7]/60 dark:bg-[#27272A]">
            <motion.div
              className="absolute inset-y-0 left-0 w-1/3 rounded-full bg-gradient-to-r from-transparent via-[#00D387] to-transparent"
              animate={{ x: ['-100%', '300%'] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

/* Tiny inline variant — replaces single-purpose `<Loader2 className="animate-spin" />`
   spots where a full hero is overkill (buttons, cards, list-loading).

   Props
     size      number  — px diameter (default 18)
     variant   'default' | 'light'  — color scheme. Use 'light' on dark
                                       or green button backgrounds where
                                       the green spinner would vanish.
     className string  — extra Tailwind classes (margin, position, …)
*/
export function ZertivoSpinner({
  size = 18,
  variant = 'default',
  className = '',
}) {
  const trackColor =
    variant === 'light' ? 'rgba(255,255,255,0.25)' : 'rgba(15,138,95,0.18)';
  const arcA = variant === 'light' ? '#FFFFFF' : '#0F8A5F';
  const arcB = variant === 'light' ? 'rgba(255,255,255,0.55)' : '#00D387';
  return (
    <span
      className={`relative inline-flex items-center justify-center align-middle ${className}`}
      style={{ width: size, height: size }}
      aria-hidden
      data-testid="zertivo-spinner"
    >
      <span
        className="absolute inset-0 rounded-full"
        style={{ border: `2px solid ${trackColor}` }}
      />
      <motion.span
        className="absolute inset-0 rounded-full"
        style={{
          border: '2px solid transparent',
          borderTopColor: arcA,
          borderRightColor: arcB,
        }}
        animate={{ rotate: 360 }}
        transition={{ duration: 0.9, repeat: Infinity, ease: 'linear' }}
      />
    </span>
  );
}
